import { RESOMedia } from '@aloft/toolkit-shared';

import { useEffect, useState } from 'react';

import { fetchGetBlob } from '../../../hooks/api/toolkitComponents.ts';
import { ToolkitFetcherConfig } from '../../../hooks/api/toolkitContext.ts';

/**
 * Custom hook to fetch entity photos from blob storage
 * @param entityMediaURLs - Record of entity IDs to their media URLs
 * @param fetcherOptions - Fetcher options from toolkit context
 * @returns Fetched photo blobs and whether the current fetch has finished
 */
export const useFetchEntityPhotos = (
  entityMediaURLs: Record<string, Required<RESOMedia>[]>,
  fetcherOptions: ToolkitFetcherConfig,
): { entityListingPhotos: Record<string, Blob[]>; hasCompletedFetch: boolean } => {
  const [entityListingPhotos, setEntityListingPhotos] = useState<Record<string, Blob[]>>({});
  const [hasCompletedFetch, setHasCompletedFetch] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHasCompletedFetch(false);

    const fetchPhotos = async (): Promise<Record<string, Blob[]>> =>
      Object.fromEntries(
        (
          await Promise.all(
            Object.entries(entityMediaURLs).map(async ([id, media]) => [
              id,
              await Promise.all(
                media.map(({ blobID }) =>
                  fetchGetBlob({ ...fetcherOptions, pathParams: { blobID } }),
                ),
              ),
            ]),
          )
        ).filter(([_, photos]) => !!photos) as [string, Blob[]][],
      );

    fetchPhotos()
      .then((photos) => {
        if (!cancelled) setEntityListingPhotos(photos);
      })
      .catch(() => {
        if (!cancelled) setEntityListingPhotos({});
      })
      .finally(() => {
        if (!cancelled) setHasCompletedFetch(true);
      });

    return () => {
      cancelled = true;
    };
  }, [entityMediaURLs, fetcherOptions]);

  return { entityListingPhotos, hasCompletedFetch };
};
